import { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl';

/**
 * Everything two maps would otherwise have to get right twice.
 *
 * The dispatch board and the orders screen both need a MapLibre instance, and
 * the fiddly part is not the layers -- it is the lifecycle. Each rule below was
 * paid for by a blank canvas with no error anywhere, so it lives in one place
 * where a fix reaches both callers.
 */

/*
 * Trim to undefined rather than trusting `??`.
 *
 * An unset key in a .env file arrives as an empty string, which is not nullish,
 * so `VITE_MAP_STYLE ?? BLANK_STYLE` hands MapLibre "" as its style. It then
 * attaches no style at all and never emits load or styledata, leaving a blank
 * canvas whose only symptom is one console warning.
 */
export const MAP_STYLE = import.meta.env.VITE_MAP_STYLE?.trim() || undefined;

/*
 * Default basemap.
 *
 * Without street context the map is just dots on a blank field, which is not a
 * dispatch tool. This uses the public OpenStreetMap raster tiles, which need no
 * API key and carry the required attribution.
 *
 * That service is for modest use and is not a production tile provider. For
 * deployment, self-host tiles (Protomaps ships an entire region as one .pmtiles
 * file, OpenMapTiles is the heavier alternative) and point VITE_MAP_STYLE at
 * that style URL -- which is also what keeps the whole system self-contained.
 *
 * Every key below must be present and valid. An explicit `glyphs: undefined`
 * fails style validation outright and MapLibre then attaches no style at all,
 * surfacing only as one console warning and a permanently blank canvas.
 */
let attribution = '&copy; OpenStreetMap contributors';

/** The credit for whichever tile source the admin panel names; set from the session at sign-in. */
export function setMapAttribution(text: string | null | undefined): void {
  if (text?.trim()) attribution = text.trim();
}

/*
 * Tiles come through our own server (/track/tiles, the same proxy the customer
 * tracking page uses), so the source is chosen in the admin panel under
 * Settings -> Maps and a provider's key never reaches the browser.
 */
export function basemapStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles: [window.location.origin + '/track/tiles/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 19,
        attribution,
      },
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': '#eef1f4' } },
      { id: 'osm', type: 'raster', source: 'osm', paint: { 'raster-opacity': 0.92 } },
    ],
  };
}


export interface MapInstance {
  containerRef: React.RefObject<HTMLDivElement>;
  mapRef: React.MutableRefObject<MapLibreMap | null>;
  /** True once the style is applied and `install` has run. */
  ready: boolean;
  /** True when the style never arrived, so the screen can say so. */
  stalled: boolean;
}

/**
 * Create one map for the lifetime of a component and tell you when it is usable.
 *
 * `install` adds the caller's sources, layers and handlers. It is held in a ref
 * so the map is never rebuilt because a parent re-rendered -- recreating a map
 * is far worse than a slightly stale closure.
 */
export function useMapInstance(install: (map: MapLibreMap) => void): MapInstance {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const installRef = useRef(install);
  installRef.current = install;

  /*
   * Readiness is state, not a ref: data usually arrives before the style
   * finishes loading, and a ref would leave that first payload undrawn with
   * nothing to trigger a redraw.
   */
  const [ready, setReady] = useState(false);
  /*
   * A map that never finishes loading its style looks exactly like a map with
   * no data on it: blank, silent, no error. Surface it instead of leaving
   * someone to guess whether the plan is empty or the basemap broke.
   */
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    /*
     * No `if (mapRef.current) return` guard here. Under StrictMode the effect
     * runs, tears down, and runs again; that guard can skip the second creation
     * while the first map is being removed, leaving a dead instance and a blank
     * canvas. The empty dependency list already limits this to one map per
     * mount, and the cleanup below always disposes the one it created.
     */

    /*
     * MapLibre gets its own child element rather than the ref'd div.
     *
     * Under StrictMode two map instances briefly overlap, and `map.remove()`
     * empties whatever container it was given -- so sharing one div lets the
     * dying instance tear down the live one's canvas, leaving a map that is
     * mounted, has WebGL, and never fires another style event. A private host
     * per instance makes that impossible.
     */
    const host = document.createElement('div');
    host.style.position = 'absolute';
    host.style.inset = '0';
    container.appendChild(host);

    const map = new maplibregl.Map({
      container: host,
      style: MAP_STYLE ?? basemapStyle(),
      center: [-84.35, 39.2],
      zoom: 8,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    // Dev-only handle: a map that draws nothing looks identical to one that was
    // never handed data, so being able to inspect its sources is worth two lines.
    if (import.meta.env.DEV) {
      (window as unknown as { __map?: MapLibreMap }).__map = map;
    }

    /*
     * Install when the style is ready, however that happens.
     *
     * Relying on a single `load` event is fragile: with an inline style object
     * and no remote sources the style can already be applied by the time the
     * handler attaches, and that one missed event leaves a permanently blank
     * map. Listening to every event, checking the style directly, and guarding
     * against double installation covers every ordering.
     */
    let installed = false;
    const run = () => {
      if (installed || !map.style || !map.isStyleLoaded()) return;
      installed = true;
      setStalled(false);
      installRef.current(map);
      setReady(true);
    };

    map.on('load', run);
    map.on('styledata', run);
    map.on('idle', run);

    const stallTimer = setTimeout(() => {
      if (!installed) setStalled(true);
    }, 8000);
    map.on('error', (event) => {
      console.warn('[map]', event.error?.message ?? 'unknown map error');
    });
    run();

    return () => {
      clearTimeout(stallTimer);
      map.remove();
      host.remove();
      if (mapRef.current === map) mapRef.current = null;
      setReady(false);
    };
  }, []);

  return { containerRef, mapRef, ready, stalled };
}

/**
 * Push a collection into a source.
 *
 * Warns rather than returning quietly when the source is missing: a silent
 * no-op here renders as an empty map with no error anywhere, which is a
 * miserable thing to debug.
 */
export function setData(
  map: MapLibreMap,
  id: string,
  data: GeoJSON.FeatureCollection,
): void {
  const source = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
  if (!source) {
    console.warn(`[map] source "${id}" is missing; ${data.features.length} features not drawn`);
    return;
  }
  source.setData(data as never);
}

export function emptyCollection(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c,
  );
}

/** The banner a screen shows when the basemap never arrived. */
export const STALLED_MESSAGE =
  'This is usually the tile source being unreachable, or a browser that is not compositing ' +
  'frames. Set VITE_MAP_STYLE to your own style URL to use self-hosted tiles.';
/* ------------------------------------------------------------------ *
 * Actions inside a map bubble
 * ------------------------------------------------------------------ */

/**
 * The buttons under a stop's details, as markup.
 *
 * MapLibre popups take an HTML string, so these are built rather than
 * rendered, and both maps build the same ones -- the dispatch board and the
 * orders map answer the same question about the same order and should not
 * offer different answers.
 *
 * Open is on its own line because it is the safe, common one. The other two
 * share a line beneath, which keeps a destructive button away from where a
 * thumb lands after reading the address, and makes the bubble read as "here is
 * the order, and here are two things you can do to it" rather than as three
 * equal choices.
 */
export function popActions(show: { unassign: boolean; remove: boolean }): string {
  if (!show.unassign && !show.remove) return '';
  return (
    '<div class="map-pop-actions">' +
    (show.unassign
      ? '<button type="button" class="btn btn-small map-pop-unassign">Unassign</button>'
      : '') +
    (show.remove
      ? '<button type="button" class="btn btn-small btn-danger map-pop-delete">Delete</button>'
      : '') +
    '</div>'
  );
}

/**
 * Attach the handlers once the popup is on the map.
 *
 * The element does not exist until then, and MapLibre discards the node when
 * the popup closes -- which takes the listeners with it, so there is nothing
 * to detach beyond removing the popup itself.
 */
export function wirePopActions(
  popup: { getElement: () => HTMLElement | undefined },
  on: { open?: () => void; unassign?: () => void; remove?: () => void },
): void {
  const root = popup.getElement();
  if (!root) return;
  const bind = (selector: string, handler?: () => void) => {
    if (!handler) return;
    root.querySelector<HTMLButtonElement>(selector)?.addEventListener('click', handler);
  };
  bind('.map-pop-action', on.open);
  bind('.map-pop-unassign', on.unassign);
  bind('.map-pop-delete', on.remove);
}
